import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicBillingPlans } from '@/services/billing';
import type { BillingPlan } from '@/types/billing';

export default function PlansSection() {
  const navigate = useNavigate();
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPublicBillingPlans()
      .then((plans) => {
        if (!cancelled) setBillingPlans(plans);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Show max 4 plans, highlight Pro
  const displayPlans = billingPlans.filter((p) => p.slug !== 'free').slice(0, 4);

  return (
    <section id="planes" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
            <i className="ri-price-tag-3-line text-xs"></i>
            Planes
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
            Elige el plan ideal{' '}
            <span className="text-primary-600">para tu radio</span>
          </h2>
          <p className="mt-4 text-foreground-500 text-base max-w-2xl mx-auto leading-relaxed">
            Los planes se basan en las herramientas que necesitás para hacer crecer tu comunidad: almacenamiento, lives, app propia y más. Escalá según el tamaño de tu audiencia.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {displayPlans.map((plan) => {
              const isHl = plan.slug === 'pro';
              const isFree = plan.price_monthly === 0;

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-6 md:p-8 flex flex-col transition-all duration-300 ${
                    isHl
                      ? 'bg-primary-600 text-background-50 ring-4 ring-primary-200 scale-[1.02] md:scale-105 z-10 shadow-2xl'
                      : 'bg-background-100 border border-background-200 hover:border-primary-200 hover:shadow-lg'
                  }`}
                >
                  {isHl && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-accent-500 text-background-50">
                      Más popular
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-xl font-bold mb-1 ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-1">
                      {isFree ? (
                        <span className={`text-3xl md:text-4xl font-extrabold ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                          Gratis
                        </span>
                      ) : (
                        <span className={`text-3xl md:text-4xl font-extrabold ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                          {plan.formatted_price_monthly}
                        </span>
                      )}
                    </div>
                    <p className={`mt-3 text-sm leading-relaxed ${isHl ? 'text-background-50/70' : 'text-foreground-500'}`}>
                      {plan.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {[
                      plan.has_domain ? 'Dominio personalizado' : `${plan.storage_gb} GB almacenamiento`,
                      plan.has_lives ? 'Lives y transmisiones' : 'Stream URL para transmitir',
                      plan.has_app ? 'App personalizada' : 'Perfil profesional',
                      plan.has_managed_streaming ? 'Streaming administrado RFW' : 'Cabina con chat',
                      plan.has_autodj ? 'AutoDJ incluido' : 'Redes sociales',
                    ].map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${
                          isHl ? 'bg-background-50/20 text-background-50' : 'bg-primary-100 text-primary-600'
                        }`}>
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        <span className={`text-sm ${isHl ? 'text-background-50/90' : 'text-foreground-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/register-radio')}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isHl
                        ? 'bg-background-50 text-primary-600 hover:bg-background-100 shadow-lg'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600 shadow-lg shadow-primary-500/20'
                    }`}
                  >
                    Comenzar con {plan.name}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}